import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Tag, Space, message, Popconfirm, Card, Typography, Row, Col, Badge } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, GiftOutlined, CalendarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { all_routes } from '../../../../router/all_routes';
import referralCampaignService from '../../../../services/super-admin/referral-campaign.service';
import { ReferralCampaign } from '../../../../types/referral.types';
import { CodeType } from '../../../../types/referral.types';
import moment from 'moment';
import FooterSection from '../../../shared/components/footerSection';
import { formatRewardValue, getRewardTypeLabel } from '../../../../utils/referralUtils';
import { RewardType } from '../../../../types/referral.types';

const { Title, Text } = Typography;

// Pagination stil optimizasyonu
const paginationStyle = `
  .ant-pagination-options-quick-jumper {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ant-pagination-options-quick-jumper > span {
    white-space: nowrap;
  }
  .ant-pagination-options-quick-jumper input {
    border-radius: 6px;
    width: 60px;
    padding: 4px 11px;
  }
  /* Pagination container'ı optimize et */
  .ant-pagination {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  @media (max-width: 768px) {
    .ant-pagination {
      flex-direction: column;
      align-items: flex-start;
    }
    .ant-pagination-options {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;

const CampaignList: React.FC = () => {
  const [campaigns, setCampaigns] = useState<ReferralCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const data = await referralCampaignService.getCampaigns();
      setCampaigns(data);
    } catch (error: any) {
      message.error('Kampanyalar yüklenirken bir hata oluştu.');
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await referralCampaignService.deleteCampaign(id);
      message.success('Kampanya başarıyla silindi.');
      fetchCampaigns();
    } catch (error: any) {
      message.error('Kampanya silinirken bir hata oluştu.');
      console.error('Error deleting campaign:', error);
    }
  };

  const columns: ColumnsType<ReferralCampaign> = [
    {
      title: 'Kampanya Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (text: string, record: ReferralCampaign) => (
        <div>
          <div style={{ fontWeight: 600, color: '#262626', marginBottom: '4px' }}>
            {text}
          </div>
          {record.description && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.description.length > 50 ? `${record.description.substring(0, 50)}...` : record.description}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Badge 
          status={isActive ? 'success' : 'default'} 
          text={
            <Tag 
              color={isActive ? 'success' : 'default'}
              style={{ 
                borderRadius: '6px',
                fontWeight: 500,
                border: 'none'
              }}
            >
              {isActive ? 'Aktif' : 'Pasif'}
            </Tag>
          }
        />
      ),
      responsive: ['md', 'lg', 'xl'],
    },
    // v2.0: Kod türü kolonu kaldırıldı - artık her şirket için kalıcı, çok kullanımlı kodlar var
    {
      title: 'Yeni Şirket Ödülü',
      key: 'newCompanyReward',
      render: (_, record: ReferralCampaign) => (
        <div>
          <Text strong style={{ color: '#7338a1' }}>
            {formatRewardValue(record.newCompanyRewardType, record.newCompanyRewardValue)}
          </Text>
          <br />
          <Tag color="purple" style={{ marginTop: '4px', borderRadius: '4px' }}>
            {getRewardTypeLabel(record.newCompanyRewardType)}
          </Tag>
        </div>
      ),
      responsive: ['lg', 'xl'],
    },
    {
      title: 'Referans Eden Ödülü',
      key: 'referringCompanyReward',
      render: (_, record: ReferralCampaign) => (
        <div>
          <Text strong style={{ color: '#f59e0b' }}>
            {formatRewardValue(record.referringCompanyRewardType, record.referringCompanyRewardValue)}
          </Text>
          <br />
          <Tag color="gold" style={{ marginTop: '4px', borderRadius: '4px' }}>
            {getRewardTypeLabel(record.referringCompanyRewardType)}
          </Tag>
        </div>
      ),
      responsive: ['xl'],
    },
    {
      title: 'Oluşturulma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <div>
          <CalendarOutlined style={{ marginRight: '4px', color: '#8c8c8c' }} />
          <Text>{moment(date).format('DD.MM.YYYY')}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {moment(date).format('HH:mm')}
          </Text>
        </div>
      ),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      responsive: ['lg', 'xl'],
    },
    {
      title: 'İşlemler',
      key: 'actions',
      fixed: 'right' as const,
      width: 220,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => navigate(all_routes.superAdminReferralCampaignDetails.replace(':id', record.id))}
            size="small"
            style={{
              background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 100%)',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 500
            }}
          >
            Detay
          </Button>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => navigate(all_routes.superAdminReferralCampaignEdit.replace(':id', record.id))}
            size="small"
            style={{
              borderRadius: '6px',
              fontWeight: 500
            }}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Kampanyayı silmek istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
            okButtonProps={{ danger: true }}
          >
            <Button 
              type="default" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
              style={{
                borderRadius: '6px',
                fontWeight: 500
              }}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // İstatistikler hesapla
  const activeCampaigns = campaigns.filter(c => c.isActive).length;
  const inactiveCampaigns = campaigns.filter(c => !c.isActive).length;
  const totalCampaigns = campaigns.length;

  return (
    <>
      <style>{paginationStyle}</style>
      {/* Page Wrapper */}
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
            <div className="my-auto mb-2">
              <h2 className="mb-1" style={{ color: '#7338a1', fontWeight: 'bold' }}>
                <GiftOutlined style={{ color: '#7338a1', marginRight: '8px' }} />
                Referans Kampanyaları
              </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminDashboard2}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Referans Kampanyaları
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons ms-2">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(all_routes.superAdminReferralCampaignCreate)}
                size="large"
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 100%)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(115, 56, 161, 0.3)',
                }}
              >
                Yeni Kampanya Oluştur
              </Button>
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Hero Section */}
          <Card
            style={{
              background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 50%, #c77dff 100%)',
              borderRadius: '16px',
              padding: '32px 24px',
              marginBottom: '24px',
              border: 'none',
              boxShadow: '0 8px 24px rgba(115, 56, 161, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Dekoratif arka plan */}
            <div style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '250px',
              height: '250px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-60px',
              left: '-60px',
              width: '200px',
              height: '200px',
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={12}>
                  <div style={{ color: 'white' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      marginBottom: '16px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <GiftOutlined style={{ fontSize: '40px', color: 'white' }} />
                    </div>
                    <Title level={2} style={{ color: 'white', marginBottom: '8px', fontWeight: 'bold' }}>
                      Referans Kampanya Yönetimi
                    </Title>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
                      Tüm referans kampanyalarınızı yönetin, yeni kampanyalar oluşturun ve istatistikleri takip edin.
                    </Text>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <Row gutter={[16, 16]}>
                    <Col xs={8} sm={8}>
                      <div style={{ textAlign: 'center', color: 'white' }}>
                        <div style={{
                          fontSize: '32px',
                          fontWeight: 'bold',
                          marginBottom: '4px'
                        }}>
                          {totalCampaigns}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>
                          Toplam Kampanya
                        </div>
                      </div>
                    </Col>
                    <Col xs={8} sm={8}>
                      <div style={{ textAlign: 'center', color: 'white' }}>
                        <div style={{
                          fontSize: '32px',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          color: '#52c41a'
                        }}>
                          {activeCampaigns}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>
                          Aktif Kampanya
                        </div>
                      </div>
                    </Col>
                    <Col xs={8} sm={8}>
                      <div style={{ textAlign: 'center', color: 'white' }}>
                        <div style={{
                          fontSize: '32px',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          color: '#ff4d4f'
                        }}>
                          {inactiveCampaigns}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>
                          Pasif Kampanya
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          </Card>

          {/* Kampanya Tablosu */}
          <Card
            style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 4px 16px rgba(115, 56, 161, 0.1)',
            }}
          >
            <Table
              columns={columns}
              dataSource={campaigns}
              rowKey="id"
              loading={loading}
              scroll={{ x: 'max-content' }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} arası, toplam ${total} kampanya`,
                locale: {
                  jump_to: 'Git',
                  page: 'Sayfa',
                  prev_page: 'Önceki Sayfa',
                  next_page: 'Sonraki Sayfa',
                  prev_5: 'Önceki 5 Sayfa',
                  next_5: 'Sonraki 5 Sayfa',
                  prev_3: 'Önceki 3 Sayfa',
                  next_3: 'Sonraki 3 Sayfa',
                },
                responsive: true,
              }}
              size="middle"
              rowClassName={(record) => 
                record.isActive ? 'campaign-row-active' : 'campaign-row-inactive'
              }
            />
          </Card>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>
      {/* /Page Wrapper */}
      
      {/* Tablo satırları için özel CSS */}
      <style>{`
        .campaign-row-active {
          background: linear-gradient(90deg, rgba(115, 56, 161, 0.05) 0%, rgba(157, 78, 221, 0.02) 100%);
        }
        .campaign-row-active:hover {
          background: linear-gradient(90deg, rgba(115, 56, 161, 0.1) 0%, rgba(157, 78, 221, 0.05) 100%) !important;
        }
        .campaign-row-inactive:hover {
          background: rgba(0, 0, 0, 0.02) !important;
        }
        .ant-table-thead > tr > th {
          background: linear-gradient(135deg, #7338a1 0%, #9d4edd 100%);
          color: white;
          font-weight: 600;
          border: none;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f0f0f0;
        }
        .ant-tag {
          border-radius: 6px;
          padding: 4px 12px;
          font-weight: 500;
        }
      `}</style>
    </>
  );
};

export default CampaignList;

